import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Alert,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  Fade,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import HelpIcon from '@mui/icons-material/Help';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import openRouterIcon from '../assets/openrouter.jpeg';
import chatGptIcon from '../assets/chatgpt.jpg';
import ollamaIcon from '../assets/ollama.png';
import googleAiIcon from '../assets/googleai.svg';
import groqIcon from '../assets/groq.svg';
import PersonIcon from '@mui/icons-material/Person';
import ClearIcon from '@mui/icons-material/Clear';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { styled } from '@mui/material/styles';
import { ollamaService, openaiService, googleAiStudioService, openRouterService, groqService, claudeService, chatService } from '../services/api';
import nistService from '../services/nistService';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Styled Components
const ChatContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.default,
  [theme.breakpoints.down('md')]: {
    height: 'auto',
    minHeight: 'calc(100vh - 120px)',
  }
}));



const ChatPanel = styled(Paper)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[4],
  backgroundColor: theme.palette.background.paper,
  overflow: 'hidden',
  margin: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    margin: theme.spacing(1),
    minHeight: '60vh',
  }
}));

const MessageBubble = styled(Box)(({ theme, isUser, isSystem }) => ({
  maxWidth: '85%',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(1.5),
  position: 'relative',
  wordWrap: 'break-word',
  animation: 'fadeInUp 0.3s ease-out',
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  ...(isUser ? {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    marginLeft: 'auto',
    borderBottomRightRadius: theme.spacing(0.5),
  } : isSystem ? {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
    margin: '0 auto',
    textAlign: 'center',
    fontStyle: 'italic',
  } : {
    backgroundColor: '#1e293b',
    color: theme.palette.text.primary,
    marginRight: 'auto',
    borderBottomLeftRadius: theme.spacing(0.5),
  })
}));

const InputArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  gap: theme.spacing(2),
  alignItems: 'flex-end',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1.5, 3),
  minWidth: 120,
  boxShadow: theme.shadows[2],
  '&:hover': {
    boxShadow: theme.shadows[4],
    transform: 'translateY(-1px)',
  },
  transition: 'all 0.2s ease-in-out',
}));

function AIChat() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const messagesEndRef = useRef(null);
  
  // Core state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [helpType, setHelpType] = useState('general'); // 'general' or 'nist'
  const [selectedModel, setSelectedModel] = useState('');
  
  // Chat session state (persistent)
  const [chatSessions, setChatSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  
  // UI state
  const [showHistoryPanel, setShowHistoryPanel] = useState(true);
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // Legacy state (for backward compatibility)
  const [sessionId, setSessionId] = useState(null);
  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  
  // RAG Health Check state
  const [ragHealthStatus, setRagHealthStatus] = useState(null);
  const [checkingRAGHealth, setCheckingRAGHealth] = useState(false);
  
  // Online AI Models state
  const [onlineModels, setOnlineModels] = useState([]);
  const [loadingOnlineModels, setLoadingOnlineModels] = useState(false);
  const [selectedOnlineModel, setSelectedOnlineModel] = useState('');
  const [freeOnlineModels, setFreeOnlineModels] = useState([]);
  const [loadingFreeOnlineModels, setLoadingFreeOnlineModels] = useState(false);
  const [selectedFreeOnlineModel, setSelectedFreeOnlineModel] = useState('');
  // The current selection, readable from fetchFreeOnlineModels. That callback
  // has no dependencies, so it sees the selection as it was on first render
  // (always empty) and would replace a model the user picked while the Groq
  // list was still loading with the Llama 4 Scout default.
  const hasModelSelectedRef = useRef(false);
  hasModelSelectedRef.current = Boolean(selectedModel || selectedOnlineModel || selectedFreeOnlineModel);
  const [claudeModels, setClaudeModels] = useState([]);
  const [loadingClaudeModels, setLoadingClaudeModels] = useState(false);
  const [hasAutoSelectedLocalModel, setHasAutoSelectedLocalModel] = useState(false);
  
  // Add state for editing a user message
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingMessageText, setEditingMessageText] = useState('');
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [copiedAllChat, setCopiedAllChat] = useState(false);
  
  const [ragMenuAnchor, setRagMenuAnchor] = useState(null);

  // Chat history search
  const [historySearchQuery, setHistorySearchQuery] = useState('');

  // UX improvement state
  const [showAdvancedModels, setShowAdvancedModels] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // AI Vendor cascading menu state
  const [vendorMenuAnchor, setVendorMenuAnchor] = useState(null);
  const [activeVendor, setActiveVendor] = useState(null);
  const [vendorSubMenuAnchor, setVendorSubMenuAnchor] = useState(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchSessions = useCallback(async () => {
    setLoadingSessions(true);
    try {
      const sessions = await chatService.listSessions();
      setChatSessions(sessions);
      if (sessions.length > 0) {
        setCurrentSessionId(sessions[0].id);
      }
    } catch (err) {
      setError('Failed to load chat sessions');
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  const fetchModels = useCallback(async () => {
    try {
      setLoadingModels(true);
      const response = await ollamaService.getModels();
      // Filter out embedding models from the Local LLMs list
      const allModels = response.models || [];
      const filteredModels = allModels.filter(model => {
        const name = (model.name || '').toLowerCase();
        return !name.includes('embed');
      });
      setModels(filteredModels);
      
      // Show warning if Ollama timeout occurred
      if (response.error) {
        setError(`Warning: ${response.error}`);
      }
      
      // Local-model auto-select removed to match Audit Window default behavior.
      // Default selection is handled by fetchFreeOnlineModels (Llama 4 Scout 17B / Groq).
    } catch (err) {
      console.error('Error fetching models:', err);
      // Ollama being unreachable is expected when it's not deployed — silently set empty models
      setModels([]);
    } finally {
      setLoadingModels(false);
    }
  }, [selectedModel, selectedOnlineModel, selectedFreeOnlineModel, hasAutoSelectedLocalModel]);

  const fetchOnlineModels = useCallback(async () => {
    try {
      setLoadingOnlineModels(true);
      const response = await openaiService.getModels();
      setOnlineModels(response.models || []);
      // No default model selection - user must explicitly select
    } catch (err) {
      console.error('Error fetching online models:', err);
      // Don't set error for online models as they're optional
    } finally {
      setLoadingOnlineModels(false);
    }
  }, []);

  const fetchFreeOnlineModels = useCallback(async () => {
    setLoadingFreeOnlineModels(true);
    try {
      let allModels = [];

      // Fetch OpenRouter free LLMs
      try {
        const openrouterResponse = await openRouterService.getModels();
        allModels = openrouterResponse.models || [];
      } catch (err) {
        console.error('Error fetching OpenRouter free models:', err);
      }

      // Fetch Google AI Studio models
      try {
        const googleResponse = await googleAiStudioService.getModels();
        const googleModels = (googleResponse.models || []).map(model => ({
          ...model,
          // Backend already provides the model name with googleaistudio: prefix
          pricing: { prompt: '0', completion: '0' },
          context_length: model.context_length || 32000
        }));
        allModels = [...allModels, ...googleModels];
      } catch (err) {
        console.error('Error fetching Google AI Studio models:', err);
      }

      // Fetch Groq models
      try {
        const groqResponse = await groqService.getModels();
        const groqModels = (groqResponse.models || []).map(model => ({
          ...model,
          // Backend already provides the model name with groq: prefix
          pricing: { prompt: '0', completion: '0' },
          context_length: model.context_length || 128000
        }));
        allModels = [...allModels, ...groqModels];
      } catch (err) {
        console.error('Error fetching Groq models:', err);
      }

      // Sort models: Groq first (with recommended at top), then Google AI Studio, then OpenRouter
      const sortedModels = allModels.sort((a, b) => {
        const aIsGoogle = a.name?.startsWith('googleaistudio:');
        const bIsGoogle = b.name?.startsWith('googleaistudio:');
        const aIsGroq = a.name?.startsWith('groq:');
        const bIsGroq = b.name?.startsWith('groq:');

        // Groq models come first
        if (aIsGroq && !bIsGroq) return -1;
        if (!aIsGroq && bIsGroq) return 1;

        // Within Groq, prioritize recommended models (Llama 3.3 70B)
        if (aIsGroq && bIsGroq) {
          const aRecommended = a.recommended === true;
          const bRecommended = b.recommended === true;
          if (aRecommended && !bRecommended) return -1;
          if (!aRecommended && bRecommended) return 1;
        }

        // Google AI Studio models come second (after Groq)
        if (aIsGoogle && !bIsGoogle && !bIsGroq) return -1;
        if (!aIsGoogle && bIsGoogle && !aIsGroq) return 1;

        // Within Google AI Studio, prioritize Gemini 2.5 Flash-Lite
        if (aIsGoogle && bIsGoogle) {
          const aIsFlashLite = a.name?.toLowerCase().includes('flash-lite') || a.name?.toLowerCase().includes('2.5-flash-lite');
          const bIsFlashLite = b.name?.toLowerCase().includes('flash-lite') || b.name?.toLowerCase().includes('2.5-flash-lite');
          if (aIsFlashLite && !bIsFlashLite) return -1;
          if (!aIsFlashLite && bIsFlashLite) return 1;
        }

        // Within same group, preserve backend order (recommended first)
        const aRecommended = a.recommended === true;
        const bRecommended = b.recommended === true;
        if (aRecommended && !bRecommended) return -1;
        if (!aRecommended && bRecommended) return 1;
        return 0;
      });

      setFreeOnlineModels(sortedModels);
      // Auto-select Llama 4 Scout 17B (Groq) as default — mirrors Audit Window default
      if (!hasModelSelectedRef.current) {
        const llama4Scout = sortedModels.find(m => m.name?.includes('llama-4-scout-17b'));
        const defaultModel = llama4Scout;
        if (defaultModel) {
          setSelectedFreeOnlineModel(defaultModel.name);
        }
      }
    } catch (err) {
      console.error('Error fetching free online models:', err);
      setFreeOnlineModels([]);
    } finally {
      setLoadingFreeOnlineModels(false);
    }
  }, []);

  const fetchClaudeModels = useCallback(async () => {
    setLoadingClaudeModels(true);
    try {
      const response = await claudeService.getModels();
      setClaudeModels(response.models || []);
    } catch (err) {
      console.error('Error fetching Claude models:', err);
    } finally {
      setLoadingClaudeModels(false);
    }
  }, []);

  // Fetch messages when currentSessionId changes
  useEffect(() => {
    if (currentSessionId) {
      fetchMessages(currentSessionId);
    } else {
      setMessages([]);
    }
  }, [currentSessionId]);

  const fetchMessages = async (sessionId) => {
    setLoadingMessages(true);
    try {
      const msgs = await chatService.listMessages(sessionId);
      setMessages(msgs);
    } catch (err) {
      setError('Failed to load chat messages');
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Create new chat session
  const createNewChat = useCallback(async () => {
    if (isCreatingChat) return; // Prevent multiple simultaneous calls
    
    setError(null);
    setIsCreatingChat(true);
    try {
      const session = await chatService.createSession('New Chat');
      setChatSessions(prev => [session, ...prev]);
      setCurrentSessionId(session.id);
      setMessages([]);
    } catch (err) {
      setError('Failed to create new chat');
    } finally {
      setIsCreatingChat(false);
    }
  }, [isCreatingChat]);

  // Select a chat session
  const selectConversation = (sessionId) => {
    setCurrentSessionId(sessionId);
    setError(null);
  };

  // Delete a chat session
  const deleteConversation = async (sessionId) => {
    const isCurrentChat = currentSessionId === sessionId;
    try {
      await chatService.deleteSession(sessionId);
      setChatSessions(prev => prev.filter(chat => chat.id !== sessionId));
      if (isCurrentChat) {
        if (chatSessions.length > 1) {
          // Select next available session
          const next = chatSessions.find(chat => chat.id !== sessionId);
          setCurrentSessionId(next?.id || null);
        } else {
          // No sessions left, create new
          createNewChat();
        }
      }
    } catch (err) {
      setError('Failed to delete chat session');
    }
  };

  // Rename a chat session
  const updateConversationTitle = async (sessionId, newTitle) => {
    const trimmedTitle = newTitle.trim();
    if (trimmedTitle.length === 0) {
      setEditingConversationId(null);
      setEditingTitle('');
      return;
    }
    try {
      const updated = await chatService.renameSession(sessionId, trimmedTitle);
      setChatSessions(prev => prev.map(chat => chat.id === sessionId ? { ...chat, title: updated.title } : chat));
    } catch (err) {
      setError('Failed to rename chat session');
    }
    setEditingConversationId(null);
    setEditingTitle('');
  };

  // Refactor handleSend to accept an optional message argument
  const handleSend = async (customMessage) => {
    // Handle case where event object might be passed (e.g., from onClick)
    let messageToSend;
    if (customMessage === undefined || customMessage === null) {
      messageToSend = input;
    } else if (typeof customMessage === 'string') {
      messageToSend = customMessage;
    } else if (customMessage.target && typeof customMessage.target.value === 'string') {
      // Handle event object with target.value
      messageToSend = customMessage.target.value;
    } else {
      // Fallback: use input state
      messageToSend = input;
    }
    
    const activeModel = getActiveModel();
    
    // Validation checks - ensure messageToSend is a string
    const messageString = String(messageToSend || '').trim();
    if (!messageString) {
      setError('Please enter a message');
      return;
    }
    
    if (!currentSessionId) {
      setError('No chat session available');
      return;
    }
    
    if (helpType !== 'nist' && !activeModel) {
      setError('Please select an AI model (offline or online) before sending a message.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    // Check if we're editing a message
    const isEditing = editingMessageId !== null;
    let editedMessage = null;
    let messageTimestamp = null;
    
    if (isEditing) {
      // Find the message being edited
      editedMessage = messages.find(msg => msg.id === editingMessageId);
      if (editedMessage) {
        messageTimestamp = editedMessage.timestamp;
        // Update the message content in the database
        try {
          await chatService.updateMessage(currentSessionId, editingMessageId, messageString);
          // Delete all messages after this one (AI response and subsequent messages)
          // Convert timestamp to ISO string format for the API
          const timestampStr = typeof messageTimestamp === 'string' 
            ? messageTimestamp 
            : new Date(messageTimestamp).toISOString();
          await chatService.deleteMessagesAfter(currentSessionId, timestampStr);
          // Remove messages from local state that come after the edited message
          setMessages(prev => {
            const editedIndex = prev.findIndex(msg => msg.id === editingMessageId);
            if (editedIndex >= 0) {
              // Keep messages up to and including the edited message, update its content
              const updatedMessages = prev.slice(0, editedIndex + 1);
              updatedMessages[editedIndex] = { ...updatedMessages[editedIndex], content: messageString };
              return updatedMessages;
            }
            return prev;
          });
        } catch (err) {
          console.error('Error updating/deleting messages:', err);
          setError('Failed to update message');
          setLoading(false);
          return;
        }
      }
      // Clear editing state
      setEditingMessageId(null);
      setEditingMessageText('');
    }
    
    try {
      // If editing, we've already updated the message, so we don't need to add a new one
      // But we need to get the updated message for the API call
      let userMsg;
      if (isEditing && editedMessage) {
        // Use the updated message
        userMsg = { ...editedMessage, content: messageString };
      } else {
        // Add new user message
        userMsg = await chatService.addMessage(currentSessionId, 'user', messageString);
        setMessages(prev => [...prev, userMsg]);
      }
      let response;
      if (helpType === 'nist') {
        try {
          // Build conversation history from current messages (last 10)
          const history = messages.slice(-10).map(m => ({
            role: m.role === 'ai' ? 'ai' : 'user',
            content: m.content
          }));
          // Pass the active model for RAG + LLM hybrid
          const activeModelForNist = getActiveModel();
          response = await nistService.chat(messageString, activeModelForNist || null, history);

          let fullAnswer = response.answer || '';

          const nistModelName = activeModelForNist
            ? `NIST RAG + ${activeModelForNist.split(':').pop()}`
            : 'NIST SP 800-53 RAG';
          const aiMsg = await chatService.addMessage(currentSessionId, 'ai', fullAnswer, nistModelName);
          setMessages(prev => [...prev, aiMsg]);
        } catch (nistErr) {
          const errorMsg = nistErr.message || 'Failed to get response from NIST RAG';
          const errorAiMsg = await chatService.addMessage(currentSessionId, 'ai', `Error: ${errorMsg}`, 'NIST SP 800-53 RAG');
          setMessages(prev => [...prev, errorAiMsg]);
          setError(errorMsg);
        }
      } else {
        // General mode - generic chat via selected model
        try {
          // Pass currentSessionId to maintain conversation context from database
          // When editing, the backend will automatically use the updated conversation context
          response = await ollamaService.ask(messageString, activeModel, null, helpType, currentSessionId);
          // Get model display name
          let modelDisplayName = activeModel || 'Unknown Model';
          if (selectedOnlineModel) {
            const onlineModel = freeOnlineModels.find(m => m.name === selectedOnlineModel);
            modelDisplayName = onlineModel?.display_name || onlineModel?.name || selectedOnlineModel;
          } else if (activeModel) {
            // For Ollama models, use the model name directly
            modelDisplayName = activeModel;
          }
          const aiMsg = await chatService.addMessage(currentSessionId, 'ai', response.answer || response, modelDisplayName);
          setMessages(prev => [...prev, aiMsg]);
        } catch (ollamaErr) {
          const errorMsg = ollamaErr.message || 'Failed to get response from AI model';
          // Get model display name for error message
          let modelDisplayName = activeModel || 'Unknown Model';
          if (selectedOnlineModel) {
            const onlineModel = freeOnlineModels.find(m => m.name === selectedOnlineModel);
            modelDisplayName = onlineModel?.display_name || onlineModel?.name || selectedOnlineModel;
          } else if (activeModel) {
            modelDisplayName = activeModel;
          }
          const errorAiMsg = await chatService.addMessage(currentSessionId, 'ai', `Error: ${errorMsg}`, modelDisplayName);
          setMessages(prev => [...prev, errorAiMsg]);
          setError(errorMsg);
        }
      }
      // If first message, update title
      const chat = chatSessions.find(c => c.id === currentSessionId);
      if (chat && (chat.title === 'New Chat' || !chat.title)) {
        const cleanInput = messageString;
        let newTitle;
        if (cleanInput.length <= 40) {
          newTitle = cleanInput;
        } else {
          const breakPoints = ['.', '!', '?', ',', ';', ' '];
          let breakIndex = -1;
          for (const point of breakPoints) {
            const index = cleanInput.indexOf(point, 30);
            if (index > 0 && index <= 40) {
              breakIndex = index;
              break;
            }
          }
          if (breakIndex > 0) {
            newTitle = cleanInput.substring(0, breakIndex + 1).trim();
          } else {
            newTitle = cleanInput.substring(0, 40) + '...';
          }
        }
        updateConversationTitle(currentSessionId, newTitle);
      }
    } catch (err) {
      console.error('Error in handleSend:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        helpType: helpType,
      });

      let errorMessage = 'Failed to send message';
      {
        if (!getActiveModel()) {
          errorMessage = 'Please select an AI model (offline or online) before sending a message.';
        } else if (err.response?.data?.detail) {
          errorMessage = `AI model error: ${err.response.data.detail}`;
        } else {
          errorMessage = 'Failed to get AI response. Please try again.';
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
      if (customMessage === undefined) setInput('');
      // Refresh messages from database to ensure consistency after edit
      if (isEditing && currentSessionId) {
        fetchMessages(currentSessionId);
      }
    }
  };

  const handleHelpTypeChange = (event) => {
    const value = event.target.value;
    setHelpType(value);
    let newSystemMessage = '';
    if (value === 'general') {
      newSystemMessage = '🧠 General Mode: Ask me anything. Responses are not limited to firewall or network security topics.';
      setSessionId(Math.random().toString(36).substring(7));
    } else if (value === 'nist') {
      newSystemMessage = '📖 NIST Mode: RAG + LLM hybrid — queries are answered using SP 800-53 Rev 5.2.0 (1,014 controls) with AI synthesis. Select an AI model for best results.';
      setSessionId(Math.random().toString(36).substring(7));
    }
    // Only add system message if we have a current session
    if (currentSessionId) {
      setMessages(prev => [
        { 
          role: 'system', 
          content: newSystemMessage,
          timestamp: new Date()
        },
        ...prev
      ]);
    }
  };

  const handleModelChange = (event) => {
    const value = event.target.value || '';
    setSelectedModel(value);
    // Always clear other model types when Local is changed
    setSelectedOnlineModel('');
    setSelectedFreeOnlineModel('');
  };

  const handleOnlineModelChange = (event) => {
    const value = event.target.value || '';
    setSelectedOnlineModel(value);
    // Always clear other model types when Paid is changed
    setSelectedModel('');
    setSelectedFreeOnlineModel('');
  };

  const handleFreeOnlineModelChange = (event) => {
    const value = event.target.value || '';
    setSelectedFreeOnlineModel(value);
    // Always clear other model types when Free is changed
    setSelectedModel('');
    setSelectedOnlineModel('');
  };


  const handleRagMenuOpen = (event) => {
    setRagMenuAnchor(event.currentTarget);
  };

  const handleRagMenuClose = () => {
    setRagMenuAnchor(null);
  };

  // Helper function to get the active model (online takes precedence)
  const getActiveModel = () => {
    return selectedFreeOnlineModel || selectedOnlineModel || selectedModel;
  };

  const formatModelSize = (size) => {
    if (size >= 1024 * 1024 * 1024) {
      return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    } else if (size >= 1024 * 1024) {
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    } else if (size >= 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    return `${size} B`;
  };

  // AI Vendor cascading menu - vendor definitions
  const vendorGroups = [
    {
      key: 'ollama',
      label: 'Ollama (Local)',
      icon: ollamaIcon,
      models: [...models].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(m => ({
        name: m.name,
        displayName: m.name,
        description: m.size ? formatModelSize(m.size) : 'Local model',
      })),
      loading: loadingModels,
    },
    {
      key: 'groq',
      label: 'Groq',
      icon: groqIcon,
      models: freeOnlineModels.filter(m => m.name?.startsWith('groq:')).map(m => ({
        name: m.name,
        displayName: m.display_name || m.name,
        description: m.description || 'Groq model',
        recommended: m.recommended,
      })),
      loading: loadingFreeOnlineModels,
    },
    {
      key: 'googleaistudio',
      label: 'Google AI Studio',
      icon: googleAiIcon,
      models: freeOnlineModels.filter(m => m.name?.startsWith('googleaistudio:')).map(m => ({
        name: m.name,
        displayName: m.display_name || m.name,
        description: m.description || 'Google AI model',
        recommended: m.recommended,
      })),
      loading: loadingFreeOnlineModels,
    },
    {
      key: 'openrouter',
      label: 'OpenRouter',
      icon: openRouterIcon,
      models: freeOnlineModels.filter(m => !m.name?.startsWith('groq:') && !m.name?.startsWith('googleaistudio:')).map(m => ({
        name: m.name,
        displayName: m.display_name || m.name,
        description: m.description || 'OpenRouter model',
        recommended: m.recommended,
      })),
      loading: loadingFreeOnlineModels,
    },
    {
      key: 'openai',
      label: 'OpenAI',
      icon: chatGptIcon,
      models: [...onlineModels].sort((a, b) => (a.display_name || a.name || '').localeCompare(b.display_name || b.name || '')).map(m => ({
        name: m.name,
        displayName: m.display_name || m.name,
        description: m.description || 'OpenAI model',
        recommended: m.recommended,
      })),
      loading: loadingOnlineModels,
    },
    {
      key: 'claude',
      label: 'Claude (Anthropic)',
      icon: null,
      models: claudeModels.map(m => ({
        name: m.name,
        displayName: m.display_name || m.name,
        description: m.description || 'Anthropic Claude model',
        recommended: m.recommended,
      })),
      loading: loadingClaudeModels,
    },
  ];

  const handleVendorMenuOpen = (event) => {
    setVendorMenuAnchor(event.currentTarget);
  };

  const handleVendorMenuClose = () => {
    setVendorMenuAnchor(null);
    setActiveVendor(null);
    setVendorSubMenuAnchor(null);
  };

  const handleVendorHover = (event, vendorKey) => {
    setActiveVendor(vendorKey);
    setVendorSubMenuAnchor(event.currentTarget);
  };

  const handleVendorModelSelect = (vendorKey, modelName) => {
    if (vendorKey === 'ollama') {
      setSelectedModel(modelName);
      setSelectedOnlineModel('');
      setSelectedFreeOnlineModel('');
    } else if (vendorKey === 'openai') {
      setSelectedOnlineModel(modelName);
      setSelectedModel('');
      setSelectedFreeOnlineModel('');
    } else if (vendorKey === 'claude') {
      setSelectedFreeOnlineModel(modelName);
      setSelectedModel('');
      setSelectedOnlineModel('');
    } else {
      // groq, googleaistudio, openrouter
      setSelectedFreeOnlineModel(modelName);
      setSelectedModel('');
      setSelectedOnlineModel('');
    }
    handleVendorMenuClose();
  };

  // Determine currently selected vendor info for the dropdown display
  const getSelectedVendorInfo = () => {
    const activeModel = selectedFreeOnlineModel || selectedOnlineModel || selectedModel;
    if (!activeModel) return null;
    for (const vendor of vendorGroups) {
      const found = vendor.models.find(m => m.name === activeModel);
      if (found) return { vendor, model: found };
    }
    return null;
  };

  // Suggested prompts for empty state
  const suggestedPrompts = [
    "Explain the key differences between stateful and stateless firewalls",
    "What are the NIST SP 800-53 recommendations for SSH hardening?",
    "Help me write an ACL rule to block traffic from a specific subnet",
    "What is the OWASP Top 10 and why does it matter?",
  ];

  // Active model display name for input indicator
  const getActiveModelDisplay = () => {
    const active = getActiveModel();
    if (!active) return null;
    const info = getSelectedVendorInfo();
    if (info) return { name: info.model.displayName, icon: info.vendor.icon, vendor: info.vendor.label };
    return { name: active, icon: null, vendor: '' };
  };

  // Markdown code block renderer
  const MarkdownCodeBlock = useMemo(() => {
    return ({ node, inline, className, children, ...props }) => {
      const match = /language-(\w+)/.exec(className || '');
      const codeString = String(children).replace(/\n$/, '');
      if (!inline && match) {
        return (
          <Box sx={{ position: 'relative', my: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#2d2d2d', px: 1.5, py: 0.5, borderRadius: '8px 8px 0 0' }}>
              <Typography variant="caption" sx={{ color: '#999', fontFamily: 'monospace' }}>{match[1]}</Typography>
              <IconButton size="small" onClick={() => navigator.clipboard.writeText(codeString)} sx={{ color: '#999', '&:hover': { color: '#fff' } }}>
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
            <SyntaxHighlighter style={oneDark} language={match[1]} PreTag="div" customStyle={{ margin: 0, borderRadius: '0 0 8px 8px', fontSize: '0.85rem' }} {...props}>
              {codeString}
            </SyntaxHighlighter>
          </Box>
        );
      }
      return <code className={className} style={{ backgroundColor: 'rgba(255,255,255,0.1)', padding: '2px 6px', borderRadius: '4px', fontSize: '0.85em' }} {...props}>{children}</code>;
    };
  }, []);

  // Date formatting for message date separators
  const formatDate = (timestamp) => {
    try {
      const date = typeof timestamp === 'string' ? new Date(timestamp.endsWith('Z') || timestamp.match(/[+-]\d{2}:\d{2}$/) ? timestamp : timestamp + 'Z') : new Date(timestamp);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
      const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      if (msgDate.getTime() === today.getTime()) return 'Today';
      if (msgDate.getTime() === yesterday.getTime()) return 'Yesterday';
      return date.toLocaleDateString('en-US', { timeZone: 'Asia/Karachi', weekday: 'short', month: 'short', day: 'numeric' });
    } catch { return ''; }
  };

  // Chat history management functions:
  const startEditingTitle = (sessionId, currentTitle) => {
    setEditingConversationId(sessionId);
    setEditingTitle(currentTitle);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Copy message content to clipboard
  const handleCopyMessage = async (messageContent, messageId) => {
    try {
      await navigator.clipboard.writeText(messageContent);
      setCopiedMessageId(messageId);
      setTimeout(() => {
        setCopiedMessageId(null);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy message:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = messageContent;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedMessageId(messageId);
        setTimeout(() => {
          setCopiedMessageId(null);
        }, 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  // Copy entire conversation to clipboard
  const handleCopyAllChat = async () => {
    try {
      const conversationText = messages.map(msg => {
        const role = msg.role === 'user' ? 'You' : msg.role === 'ai' ? 'AI Assistant' : 'System';
        const time = formatTime(msg.timestamp);
        return `${role} (${time})\n${msg.content}\n`;
      }).join('\n');
      
      await navigator.clipboard.writeText(conversationText);
      setCopiedAllChat(true);
      setTimeout(() => {
        setCopiedAllChat(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy conversation:', err);
      // Fallback for older browsers
      const conversationText = messages.map(msg => {
        const role = msg.role === 'user' ? 'You' : msg.role === 'ai' ? 'AI Assistant' : 'System';
        const time = formatTime(msg.timestamp);
        return `${role} (${time})\n${msg.content}\n`;
      }).join('\n');
      
      const textArea = document.createElement('textarea');
      textArea.value = conversationText;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedAllChat(true);
        setTimeout(() => {
          setCopiedAllChat(false);
        }, 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const clearConversationContext = async () => {
    if (sessionId) {
      try {
        await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/ollama/conversation/${sessionId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        });
      } catch (error) {
        console.error('Error clearing conversation:', error);
      }
    }
  };

  // Create initial chat session if none exist (only after initial load)
  useEffect(() => {
    if (chatSessions.length === 0 && !loadingSessions && hasInitialized && !isCreatingChat) {
      createNewChat();
    }
  }, [chatSessions.length, loadingSessions, hasInitialized, isCreatingChat, createNewChat]);

  useEffect(() => {
    if (!loadingSessions && !hasInitialized) {
      setHasInitialized(true);
    }
  }, [loadingSessions, hasInitialized]);



  const formatTime = (timestamp) => {
    try {
      let date;
      if (typeof timestamp === 'string') {
        // FastAPI sends datetime as ISO string, but may be timezone-naive
        // If it doesn't have timezone info (no Z or +/-), treat it as UTC
        let timestampStr = timestamp.trim();
        
        // Check if it has timezone info
        const hasTimezone = timestampStr.endsWith('Z') || 
                           timestampStr.match(/[+-]\d{2}:\d{2}$/) ||
                           timestampStr.match(/[+-]\d{4}$/);
        
        if (!hasTimezone && timestampStr.includes('T')) {
          // No timezone info but has date-time separator, assume UTC
          timestampStr = timestampStr + 'Z';
        }
        
        date = new Date(timestampStr);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else {
        // If it's already a Date object or number, use it directly
        date = new Date(timestamp);
      }
      
      // Ensure we have a valid date
      if (isNaN(date.getTime())) {
        console.error('Invalid timestamp:', timestamp);
        return '--:--';
      }
      
      // Convert to PKT (Pakistan Standard Time - Asia/Karachi) using Intl.DateTimeFormat
      // The timestamp from backend is in UTC (from database), so we convert to PKT
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Karachi',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true // Use 12-hour format with AM/PM
      });
      
      return formatter.format(date);
    } catch (error) {
      console.error('Error formatting time:', error, 'timestamp:', timestamp);
      // Fallback to original format if conversion fails
      try {
        const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
        return date.toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      } catch (e) {
        return '--:--';
      }
    }
  };

  // Fetch chat sessions on mount
  useEffect(() => {
    fetchSessions();
    fetchModels();
    fetchOnlineModels();
    fetchFreeOnlineModels();
    fetchClaudeModels();
  }, [fetchSessions, fetchModels, fetchOnlineModels, fetchFreeOnlineModels, fetchClaudeModels]);

  // Add ChatHistoryPanel component before the main return:
  const ChatHistoryPanel = () => (
    <Box
      sx={{
        width: 260,
        height: 'calc(100vh - 120px)', // Subtract the height of the Top Banner
        borderRight: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative', // Changed from 'fixed' to 'relative'
        zIndex: 1, // Reduced z-index since it's no longer an overlay
        transform: showHistoryPanel ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.3s ease-in-out',
        '@media (max-width: 768px)': {
          width: '100%',
          maxWidth: 300,
          position: 'fixed', // Keep fixed on mobile for overlay behavior
          left: 0,
          top: 0,
          height: '100vh',
          zIndex: 1200,
        }
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={createNewChat}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          New Chat
        </Button>
        {/* Search Conversations */}
        <TextField
          size="small"
          placeholder="Search chats..."
          value={historySearchQuery}
          onChange={(e) => setHistorySearchQuery(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, opacity: 0.5 }} />
              </InputAdornment>
            ),
            endAdornment: historySearchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setHistorySearchQuery('')} sx={{ p: 0.25 }}>
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              fontSize: '0.8rem',
              height: 36,
            },
          }}
        />
      </Box>

      {/* Chat History List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {(() => {
          const query = historySearchQuery.trim().toLowerCase();
          const filtered = query
            ? chatSessions.filter(chat => (chat.title || '').toLowerCase().includes(query))
            : chatSessions;
          if (query && filtered.length === 0) {
            return (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2, px: 1 }}>
                No chats matching "{historySearchQuery.trim()}"
              </Typography>
            );
          }
          return filtered.map((chat) => (
          <Box
            key={chat.id}
            sx={{
              p: 1,
              mb: 0.5,
              borderRadius: 1,
              cursor: 'pointer',
              backgroundColor: currentSessionId === chat.id ? theme.palette.action.selected : 'transparent',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative'
            }}
            onClick={() => selectConversation(chat.id)}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {editingConversationId === chat.id ? (
                <TextField
              size="small"
                  value={editingTitle}
                  onChange={(e) => setEditingTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      updateConversationTitle(chat.id, editingTitle);
                    } else if (e.key === 'Escape') {
                      setEditingConversationId(null);
                      setEditingTitle('');
                    }
                  }}
                  onBlur={() => updateConversationTitle(chat.id, editingTitle)}
                  autoFocus
                  sx={{ fontSize: '0.875rem' }}
                />
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: currentSessionId === chat.id ? 600 : 400,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '0.875rem'
                  }}
                >
                  {(() => {
                    const q = historySearchQuery.trim().toLowerCase();
                    const title = chat.title || '';
                    if (!q || !title.toLowerCase().includes(q)) return title;
                    const idx = title.toLowerCase().indexOf(q);
                    return (
                      <>
                        {title.slice(0, idx)}
                        <Box component="span" sx={{ backgroundColor: 'warning.main', color: 'warning.contrastText', borderRadius: '2px', px: '2px' }}>
                          {title.slice(idx, idx + q.length)}
                        </Box>
                        {title.slice(idx + q.length)}
                      </>
                    );
                  })()}
                        </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                {new Date(chat.updated_at).toLocaleDateString()}
                        </Typography>
        </Box>

            <Box sx={{ 
              display: 'flex', 
              gap: 0.5, 
              opacity: currentSessionId === chat.id ? 1 : 0,
              transition: 'opacity 0.2s ease-in-out',
              '&:hover': { opacity: 1 }
            }}>
              <IconButton
              size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  startEditingTitle(chat.id, chat.title);
                }}
                sx={{ 
                  color: 'primary.main',
                  '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                }}
                title="Rename chat"
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteConfirmId(chat.id);
                }}
                sx={{
                  color: 'error.main',
                  '&:hover': { backgroundColor: 'error.light', color: 'white' }
                }}
                title="Delete chat"
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
                </Box>
                </Box>
                  ));
        })()}
                </Box>
          </Box>
  );

  // Update the main return JSX to include the history panel:
  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 120px)' }}>
      {/* Chat History Panel - Desktop */}
      {!isMobile && <ChatHistoryPanel />}
      
      {/* Main Chat Area */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
    <ChatContainer>
          {/* Horizontal Settings Bar - Desktop */}
      {!isMobile && (
            <Box sx={{
              p: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}>
          {/* AI Model Selection Row - AI Vendor only */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flexShrink: 0 }}>
                {/* AI Vendor - Cascading Dropdown */}
                <FormControl size="small" sx={{ minWidth: 220, maxWidth: 280 }}>
                  <InputLabel shrink>Select AI Model</InputLabel>
                  <Select
                    value=""
                    open={false}
                    onOpen={handleVendorMenuOpen}
                    label="Select AI Model"
                    disabled={loading}
                    displayEmpty
                    sx={{ borderRadius: 2 }}
                    renderValue={() => {
                      const info = getSelectedVendorInfo();
                      if (!info) {
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <StorefrontIcon sx={{ fontSize: 20, opacity: 0.7 }} />
                            <Typography variant="body2" sx={{ opacity: 0.5 }}>
                              Select LLM
                            </Typography>
                          </Box>
                        );
                      }
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {info.vendor.icon ? (
                            <Box
                              component="img"
                              src={info.vendor.icon}
                              alt={info.vendor.label}
                              sx={{ width: 20, height: 20, borderRadius: '4px', objectFit: 'contain' }}
                            />
                          ) : (
                            <AutoAwesomeIcon sx={{ width: 20, height: 20, color: '#D97706' }} />
                          )}
                          <Typography variant="body2" noWrap>
                            {info.model.displayName}
                          </Typography>
                        </Box>
                      );
                    }}
                  >
                    <MenuItem value="" disabled />
                  </Select>
                </FormControl>

                {/* AI Vendor Main Menu */}
                <Menu
                  anchorEl={vendorMenuAnchor}
                  open={Boolean(vendorMenuAnchor)}
                  onClose={handleVendorMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  sx={{ '& .MuiPaper-root': { minWidth: 240, mt: 0.5 } }}
                >
                  {vendorGroups.map((vendor) => (
                    <MenuItem
                      key={vendor.key}
                      onMouseEnter={(e) => handleVendorHover(e, vendor.key)}
                      sx={{
                        py: 1.5,
                        backgroundColor: activeVendor === vendor.key ? 'action.hover' : 'transparent',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                        {vendor.icon ? (
                          <Box
                            component="img"
                            src={vendor.icon}
                            alt={vendor.label}
                            sx={{ width: 24, height: 24, borderRadius: '4px', objectFit: 'contain' }}
                          />
                        ) : (
                          <AutoAwesomeIcon sx={{ width: 24, height: 24, color: '#D97706' }} />
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {vendor.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {vendor.loading ? 'Loading...' : `${vendor.models.length} model${vendor.models.length !== 1 ? 's' : ''}`}
                          </Typography>
                        </Box>
                        <ChevronRightIcon sx={{ fontSize: 18, opacity: 0.5 }} />
                      </Box>
                    </MenuItem>
                  ))}
                </Menu>

                {/* AI Vendor Sub Menu - Models */}
                <Menu
                  anchorEl={vendorSubMenuAnchor}
                  open={Boolean(vendorSubMenuAnchor) && Boolean(activeVendor)}
                  onClose={handleVendorMenuClose}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  sx={{
                    pointerEvents: 'none',
                    '& .MuiPaper-root': {
                      pointerEvents: 'auto',
                      minWidth: 300,
                      maxHeight: 400,
                      overflow: 'auto',
                      ml: 0.5,
                    }
                  }}
                >
                  {activeVendor && (() => {
                    const vendor = vendorGroups.find(v => v.key === activeVendor);
                    if (!vendor) return null;
                    if (vendor.loading) {
                      return (
                        <MenuItem disabled>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2">Loading models...</Typography>
                          </Box>
                        </MenuItem>
                      );
                    }
                    if (vendor.models.length === 0) {
                      return (
                        <MenuItem disabled>
                          <Typography variant="body2" color="text.secondary">No models available</Typography>
                        </MenuItem>
                      );
                    }
                    const activeModel = getActiveModel();
                    return vendor.models.map((model) => (
                      <MenuItem
                        key={model.name}
                        onClick={() => handleVendorModelSelect(vendor.key, model.name)}
                        selected={model.name === activeModel}
                        sx={{ py: 1 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          {vendor.icon ? (
                            <Box
                              component="img"
                              src={vendor.icon}
                              alt={vendor.label}
                              sx={{ width: 20, height: 20, borderRadius: '4px', objectFit: 'contain' }}
                            />
                          ) : (
                            <AutoAwesomeIcon sx={{ width: 20, height: 20, color: '#D97706' }} />
                          )}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" noWrap>
                              {model.displayName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {model.description}
                            </Typography>
                          </Box>
                          {model.recommended && (
                            <Typography variant="caption" color="secondary" sx={{ fontWeight: 'bold' }}>
                              Recommended
                            </Typography>
                          )}
                          {model.name === activeModel && (
                            <CheckIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                          )}
                        </Box>
                      </MenuItem>
                    ));
                  })()}
                </Menu>

                {/* Help Type Dropdown */}
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel shrink>Help Type</InputLabel>
                  <Select
                    value={helpType}
                    onChange={handleHelpTypeChange}
                    label="Help Type"
                    disabled={loading}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="general">General Help</MenuItem>
                    <MenuItem value="nist">NIST SP 800-53</MenuItem>
                  </Select>
                </FormControl>
          </Box>

          {/* Copy All Chat Button */}
          {messages.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
              <Tooltip title={copiedAllChat ? "Copied!" : "Copy entire conversation"}>
                <IconButton
                  size="small"
                  onClick={handleCopyAllChat}
                  sx={{
                    color: copiedAllChat ? theme.palette.success.main : theme.palette.text.secondary,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                      color: theme.palette.primary.main
                    }
                  }}
                >
                  {copiedAllChat ? (
                    <CheckIcon fontSize="small" />
                  ) : (
                    <ContentCopyIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          )}

            </Box>
      )}

      {/* Chat Panel */}
      <ChatPanel>


        {/* Chat Messages */}
        <Box sx={{
          flex: 1,
          overflow: 'auto',
          p: 3,
          backgroundColor: '#0a0e1a'
        }}>
          {/* Empty State - Welcome message with suggested prompts */}
          {messages.length === 0 && !loading && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              px: 3
            }}>
              <SmartToyIcon sx={{ fontSize: 64, opacity: 0.2, mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, opacity: 0.8 }}>
                How can I help you?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4, maxWidth: 400 }}>
                Ask me anything about cybersecurity, network configuration, or firewall best practices.
              </Typography>
              <Grid container spacing={1.5} sx={{ maxWidth: 560 }}>
                {suggestedPrompts.map((prompt, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Card
                      variant="outlined"
                      onClick={() => { setInput(prompt); }}
                      sx={{
                        cursor: 'pointer',
                        borderColor: 'divider',
                        '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' },
                        transition: 'all 0.2s',
                      }}
                    >
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="caption" sx={{ lineHeight: 1.4 }}>
                          {prompt}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {messages.map((msg, idx) => {
            // Date separator logic
            const currentDate = formatDate(msg.timestamp);
            const prevDate = idx > 0 ? formatDate(messages[idx - 1].timestamp) : null;
            const showDateSeparator = currentDate && currentDate !== prevDate;

            return (
            <Fade in={true} key={idx} timeout={300}>
              <Box>
                {/* Date Separator */}
                {showDateSeparator && (
                  <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ px: 2, fontSize: '0.7rem', fontWeight: 500 }}>
                      {currentDate}
                    </Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Box>
                )}
              <Box sx={{ mb: 2 }}>
                <MessageBubble
                  isUser={msg.role === 'user'}
                  isSystem={msg.role === 'system'}
                >
                  {/* Copy button at top right */}
                  <Tooltip title={copiedMessageId === msg.id ? "Copied!" : "Copy message"}>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyMessage(msg.content, msg.id)}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: msg.role === 'user' ? 'white' : theme.palette.text.primary,
                        opacity: 0.6,
                        '&:hover': {
                          opacity: 1,
                          backgroundColor: msg.role === 'user' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)'
                        }
                      }}
                    >
                      {copiedMessageId === msg.id ? (
                        <CheckIcon fontSize="small" />
                      ) : (
                        <ContentCopyIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: msg.role === 'user' ? theme.palette.primary.main :
                                msg.role === 'ai' ? theme.palette.success.main :
                                theme.palette.info.main
                      }}
                    >
                      {msg.role === 'user' ? <PersonIcon sx={{ fontSize: 16 }} /> :
                       msg.role === 'ai' ? <SmartToyIcon sx={{ fontSize: 16 }} /> :
                       <HelpIcon sx={{ fontSize: 16 }} />}
                    </Avatar>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {msg.role === 'user' ? 'You' :
                       msg.role === 'ai' ? 'AI Assistant' : 'System'}
                    </Typography>
                    {msg.role === 'ai' && msg.model_name && (
                      <Typography variant="caption" sx={{
                        opacity: 0.7,
                        fontStyle: 'italic',
                        ml: 0.5,
                        fontSize: '0.7rem'
                      }}>
                        ({msg.model_name})
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ opacity: 0.7, ml: 1 }}>
                      {formatTime(msg.timestamp)}
                    </Typography>
                    {/* Edit icon for user messages */}
                    {msg.role === 'user' && editingMessageId !== msg.id && (
                      <Tooltip title="Edit & Resubmit">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingMessageId(msg.id);
                            setEditingMessageText(msg.content);
                          }}
                          sx={{
                            ml: 1,
                            color: 'white',
                            opacity: 0.7,
                            '&:hover': {
                              opacity: 1,
                              color: 'white'
                            }
                          }}
                        >
                          <EditIcon fontSize="small" sx={{ color: 'white' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  {/* Edit mode for user message */}
                  {editingMessageId === msg.id ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        value={editingMessageText}
                        onChange={e => setEditingMessageText(e.target.value)}
                        size="small"
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={4}
                        autoFocus
                        sx={{
                          flex: 1,
                          '& .MuiOutlinedInput-root': {
                            color: msg.role === 'user' ? theme.palette.primary.contrastText : theme.palette.text.primary,
                            backgroundColor: msg.role === 'user' ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                            '& fieldset': {
                              borderColor: msg.role === 'user' ? 'rgba(255, 255, 255, 0.3)' : theme.palette.divider,
                            },
                            '&:hover fieldset': {
                              borderColor: msg.role === 'user' ? 'rgba(255, 255, 255, 0.5)' : theme.palette.primary.main,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: msg.role === 'user' ? 'rgba(255, 255, 255, 0.7)' : theme.palette.primary.main,
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: msg.role === 'user' ? theme.palette.primary.contrastText : theme.palette.text.primary,
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: msg.role === 'user' ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary,
                            opacity: 1,
                          },
                        }}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => {
                          handleSend(editingMessageText);
                          setEditingMessageId(null);
                          setEditingMessageText('');
                        }}
                        sx={{ minWidth: 0, px: 1 }}
                      >
                        Submit
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={() => {
                          setEditingMessageId(null);
                          setEditingMessageText('');
                        }}
                        sx={{ minWidth: 0, px: 1 }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  ) : msg.role === 'ai' ? (
                    /* Markdown rendering for AI responses */
                    <Box sx={{
                      '& p': { mt: 0, mb: 1, lineHeight: 1.6 },
                      '& p:last-child': { mb: 0 },
                      '& ul, & ol': { pl: 3, mb: 1 },
                      '& li': { mb: 0.5 },
                      '& h1, & h2, & h3, & h4': { mt: 2, mb: 1, fontWeight: 600 },
                      '& blockquote': { borderLeft: '3px solid', borderColor: 'divider', pl: 2, ml: 0, opacity: 0.8 },
                      '& table': { borderCollapse: 'collapse', width: '100%', mb: 1 },
                      '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1.5, py: 0.75, fontSize: '0.85rem' },
                      '& th': { backgroundColor: 'action.hover', fontWeight: 600 },
                      '& a': { color: 'primary.light' },
                      '& hr': { borderColor: 'divider', my: 2 },
                    }}>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{ code: MarkdownCodeBlock }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                    </Box>
                  ) : (
                    <Typography variant="body1" sx={{
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.6
                    }}>
                      {msg.content}
                    </Typography>
                  )}
                </MessageBubble>
              </Box>
              </Box>
            </Fade>
            );
          })}

          {loading && (
            <Fade in={true}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.success.main, width: 32, height: 32 }}>
                  <SmartToyIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    AI Assistant
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Thinking...
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Fade>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {/* Input Area */}
        <InputArea>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {/* Active model indicator */}
            {(() => {
              const modelInfo = getActiveModelDisplay();
              return modelInfo ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1 }}>
                  {modelInfo.icon && (
                    <Box
                      component="img"
                      src={modelInfo.icon}
                      alt=""
                      sx={{ width: 14, height: 14, borderRadius: '3px', objectFit: 'contain' }}
                    />
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {modelInfo.vendor ? `${modelInfo.vendor} — ` : ''}{modelInfo.name}
                  </Typography>
                </Box>
              ) : null;
            })()}
            <StyledTextField
              fullWidth
              multiline
              minRows={1}
              maxRows={4}
              placeholder="Ask anything..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleInputKeyDown}
              disabled={loading || (helpType !== 'nist' && !getActiveModel())}
              InputProps={{
                endAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {loading && <CircularProgress size={16} />}
                    <AutoAwesomeIcon sx={{ fontSize: 20, opacity: 0.5 }} />
                  </Box>
                ),
              }}
            />
          </Box>
          <Tooltip title={loading ? 'Sending...' : 'Send message'}>
            <span>
              <IconButton
                color="primary"
                onClick={() => handleSend()}
                disabled={loading || !input.trim() || (helpType !== 'nist' && !getActiveModel())}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  width: 44,
                  height: 44,
                  '&:hover': { bgcolor: 'primary.dark' },
                  '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
                }}
              >
                {loading ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : <SendIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </InputArea>
      </ChatPanel>


    </ChatContainer>
      </Box>
      
      {/* Mobile Chat History Panel - Overlay */}
      {isMobile && <ChatHistoryPanel />}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteConfirmId)}
        onClose={() => setDeleteConfirmId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Chat</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this conversation? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              deleteConversation(deleteConfirmId);
              setDeleteConfirmId(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AIChat; 
